import { api } from '@packages/backend/convex/_generated/api';
import { getUserErrorMessage, type PosPasscode } from '@packages/shared';
import { createFileRoute } from '@tanstack/react-router';
import { useConvex } from 'convex/react';
import { useQuery } from 'convex-helpers/react';
import { useState } from 'react';
import { toast } from 'sonner';
import { ActionDropdown } from '@/components/ActionDropdown';
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ActionButton } from '@/components/ui+/ActionButton';
import { ColorBadge } from '@/components/ui+/ColorBadge';
import { LoadingIcon } from '@/components/ui+/LoadingIcon';
import { PosPasscodeForm } from './components/PosPasscodeForm';

export const Route = createFileRoute('/r/$restaurantId/s/pos-passcodes')({
  component: Component,
});

function Component() {
  const convex = useConvex();
  const { restaurantId } = Route.useParams();
  const {
    data: posConfig,
    isError,
    isPending,
  } = useQuery(api.posConfigs.getByRestaurantId, {
    restaurantId,
  });
  const [formState, setFormState] = useState<PosPasscode | 'new' | 'closed'>('closed');

  const handleDelete = async (posPasscodeUuid: string) => {
    try {
      await convex.mutation(api.posConfigs.deletePosPasscode, {
        posPasscodeUuid,
        restaurantId,
      });
      toast.success('Deleted successfully');
    } catch (error) {
      toast.error("Couldn't delete", {
        description: getUserErrorMessage(error),
      });
    }
  };

  if (isPending) return <LoadingIcon />;
  if (isError) return null;
  if (!posConfig) return <div>No config yet.</div>;

  const posPasscodes = [...(posConfig.posPasscodes ?? [])].sort((a, b) => {
    if (a.role !== b.role) {
      return a.role === 'MANAGER' ? -1 : 1;
    }
    return a.name.localeCompare(b.name);
  });

  return (
    <>
      {formState !== 'closed' && (
        <PosPasscodeForm
          editPosPasscode={formState === 'new' ? undefined : formState}
          existingPasscodes={posPasscodes.map((p) => p.passcode)}
          onClose={() => setFormState('closed')}
          restaurantId={restaurantId}
        />
      )}
      <Card>
        <CardHeader>
          <CardTitle>POS Passcodes</CardTitle>
          <CardDescription>
            Require staff to enter a passcode when using the POS system
          </CardDescription>
          <CardAction>
            <ActionButton onClick={() => setFormState('new')}>Create New</ActionButton>
          </CardAction>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Passcode</TableHead>
                <TableHead className="w-[64px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {posPasscodes.map((passcode) => (
                <TableRow key={passcode.uuid}>
                  <TableCell>{passcode.name}</TableCell>
                  <TableCell>
                    <ColorBadge color={passcode.role === 'MANAGER' ? 'purple' : 'pink'}>
                      {passcode.role}
                    </ColorBadge>
                  </TableCell>
                  <TableCell className="font-mono">{passcode.passcode}</TableCell>
                  <TableCell>
                    <ActionDropdown
                      onDelete={() => handleDelete(passcode.uuid)}
                      onEdit={() => setTimeout(() => setFormState(passcode), 0)}
                    />
                  </TableCell>
                </TableRow>
              ))}
              {posPasscodes.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground">
                    No passcodes set up yet
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </>
  );
}
